/**
 * Bundle 131 P0 #4 - Introduction detail workflow page.
 *
 * URL: /admin/introductions/:intro_id
 *
 * Personas reconciled (matchmaker + UX, docs in
 * bundle_131_routes.py):
 *
 *   Layout: sub-tabs stacked, NOT side-by-side. Info-density
 *   optimized for one-decision-at-a-time.
 *
 *   Overview tab (default):
 *     [1] Reporter context card (collapsed)
 *     [2] What They Noticed panel (full-width prose, no clipping)
 *     [3] Contact Preference pill (colored)
 *     [4] Actions bar (Open Suggestions / Escalate / Dismiss)
 *
 *   Target Suggestions tab:
 *     Full-width rich candidate cards with matched-on tags,
 *     "Pick this target" primary action.
 *
 *   Audit Trail tab:
 *     Last 10 entries, actor + timestamp + action.
 *
 * Hygiene: no em/en dashes, no eslint-disable.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/utils/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
  ArrowLeft, ChevronDown, ChevronUp, User, MapPin, Clock,
  MessageSquare, Heart, Users as UsersIcon, Sparkles, XCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const PILL_COLOR = {
  green: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  blue:  'bg-sky-50 text-sky-800 border-sky-200',
  red:   'bg-red-50 text-red-800 border-red-200',
  grey:  'bg-neutral-100 text-neutral-700 border-neutral-200',
};

const ContactPrefPill = ({ pref }) => {
  if (!pref) return null;
  return (
    <span
      className={`text-[11px] px-2 py-0.5 rounded border ${PILL_COLOR[pref.color] || PILL_COLOR.grey}`}
      data-testid="intro-contact-pref-pill"
    >
      {pref.label}
    </span>
  );
};

const ReporterCard = ({ reporter }) => {
  const [open, setOpen] = useState(false);
  const fullName = `${reporter.first_name || ''} ${reporter.last_name || ''}`.trim() || '(no name)';
  return (
    <Card data-testid="intro-reporter-card" className="border-neutral-200">
      <CardContent className="p-4">
        <button
          type="button"
          className="w-full flex items-center justify-between"
          onClick={() => setOpen((s) => !s)}
          data-testid="intro-reporter-toggle"
        >
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-primary-100 p-2">
              <User className="h-4 w-4 text-primary-800" />
            </div>
            <div className="text-left">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                Reporter
              </div>
              <div className="text-primary-900 font-semibold">
                {fullName}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px]">
              Prior events: {reporter.prior_events_count ?? 0}
            </Badge>
            {open ? <ChevronUp className="h-4 w-4" />
                  : <ChevronDown className="h-4 w-4" />}
          </div>
        </button>
        {open && (
          <div className="mt-3 grid gap-2 sm:grid-cols-3 text-xs">
            <div>
              <div className="text-neutral-500 uppercase tracking-widest text-[10px]">Email</div>
              <div className="text-primary-900 font-mono text-[11px]">
                {reporter.email || '-'}
              </div>
            </div>
            <div>
              <div className="text-neutral-500 uppercase tracking-widest text-[10px]">Age band</div>
              <div className="text-primary-900">
                {reporter.age_band || 'not captured'}
              </div>
            </div>
            <div>
              <div className="text-neutral-500 uppercase tracking-widest text-[10px]">Gender</div>
              <div className="text-primary-900">
                {reporter.gender || 'not captured'}
              </div>
            </div>
            <div className="sm:col-span-3">
              <div className="text-neutral-500 uppercase tracking-widest text-[10px]">
                Allow introductions
              </div>
              <div className="text-primary-900">
                {reporter.allow_introductions === true
                  ? 'Yes'
                  : reporter.allow_introductions === false
                    ? 'No (override required)'
                    : 'Not on file'}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const NoticedPanel = ({ noticed }) => (
  <Card data-testid="intro-noticed-panel" className="border-neutral-200">
    <CardContent className="p-4 space-y-3">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">
        What they noticed
      </div>
      <div className="text-primary-900 font-semibold">
        {noticed.target_first_name || '(no target name given)'}
      </div>
      {noticed.physical_description ? (
        <div className="rounded-md bg-neutral-50 p-3 border border-neutral-200">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
            Physical description
          </div>
          <div className="text-sm text-primary-900 whitespace-pre-wrap"
                data-testid="intro-noticed-physical">
            {noticed.physical_description}
          </div>
        </div>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2">
        {noticed.when_where ? (
          <div className="flex items-start gap-2">
            <MapPin className="h-4 w-4 text-neutral-500 mt-0.5" />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                When / where
              </div>
              <div className="text-sm text-primary-900"
                    data-testid="intro-noticed-when-where">
                {noticed.when_where}
              </div>
            </div>
          </div>
        ) : null}
        {noticed.interest_type ? (
          <div className="flex items-start gap-2">
            <Heart className="h-4 w-4 text-neutral-500 mt-0.5" />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                Interest
              </div>
              <div className="text-sm text-primary-900 capitalize">
                {noticed.interest_type}
              </div>
            </div>
          </div>
        ) : null}
      </div>
      {noticed.reason_to_reconnect ? (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
            Reason to reconnect
          </div>
          <div className="text-sm text-primary-900 whitespace-pre-wrap"
                data-testid="intro-noticed-reason">
            {noticed.reason_to_reconnect}
          </div>
        </div>
      ) : null}
      {noticed.conversation_notes ? (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
            Conversation notes
          </div>
          <div className="text-sm text-primary-900 whitespace-pre-wrap flex items-start gap-2">
            <MessageSquare className="h-4 w-4 text-neutral-500 mt-0.5" />
            <span>{noticed.conversation_notes}</span>
          </div>
        </div>
      ) : null}
    </CardContent>
  </Card>
);

const SuggestionCard = ({ target, onPick, picking }) => {
  const fullName = `${target.first_name || ''} ${target.last_name || ''}`.trim();
  const signals = target.signals || {};
  const reasons = target.match_reasons || [];
  return (
    <Card
      className="border-neutral-200 hover:border-primary-400 transition-colors"
      data-testid={`intro-suggestion-${target.user_id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-secondary-100 p-2">
              <User className="h-5 w-5 text-secondary-800" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <div className="text-primary-900 font-semibold">
                  {fullName || '(no name)'}
                </div>
                {typeof target.score === 'number' ? (
                  <span
                    className="text-[10px] font-mono rounded border px-1.5 py-0 bg-primary-50 text-primary-800 border-primary-200"
                    data-testid={`intro-suggestion-score-${target.user_id}`}
                  >
                    score {target.score.toFixed(2)}
                  </span>
                ) : null}
              </div>
              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                {target.age_band ? (
                  <Badge variant="outline" className="text-[10px]">
                    {target.age_band}
                  </Badge>
                ) : null}
                {target.gender ? (
                  <Badge variant="outline" className="text-[10px] capitalize">
                    {target.gender}
                  </Badge>
                ) : null}
                {target.checked_in_at ? (
                  <Badge className="bg-emerald-50 text-emerald-800 border-emerald-200 border text-[10px]">
                    <Clock className="h-3 w-3 mr-0.5" />
                    {new Date(target.checked_in_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Badge>
                ) : null}
                {target.allow_introductions === false ? (
                  <Badge className="bg-red-50 text-red-800 border-red-200 border text-[10px]">
                    allow_introductions=NO
                  </Badge>
                ) : null}
              </div>
              {reasons.length > 0 ? (
                <div className="mt-2 flex items-start gap-1 flex-wrap"
                        data-testid={`intro-suggestion-reasons-${target.user_id}`}>
                  <span className="text-[10px] text-neutral-500">
                    Matched on:
                  </span>
                  {reasons.map((t, i) => (
                    <Badge key={i} variant="outline"
                            className="text-[10px] bg-primary-50 text-primary-800 border-primary-200">
                      {t}
                    </Badge>
                  ))}
                </div>
              ) : null}
              {Object.keys(signals).length > 0 ? (
                <div className="mt-2 grid grid-cols-5 gap-1 max-w-md">
                  {Object.entries(signals).map(([k, v]) => (
                    <div key={k}
                          className="text-[10px] text-neutral-600 rounded bg-neutral-50 border border-neutral-200 px-1.5 py-0.5"
                          data-testid={`intro-suggestion-signal-${target.user_id}-${k}`}>
                      <div className="uppercase tracking-widest text-[9px] text-neutral-500">
                        {k.replace(/_/g, ' ')}
                      </div>
                      <div className="font-mono text-primary-900">
                        {typeof v === 'number' ? v.toFixed(2) : v}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
          <Button
            size="sm"
            className="bg-primary-800 text-white hover:bg-primary-900"
            onClick={() => onPick(target)}
            disabled={picking}
            data-testid={`intro-suggestion-pick-${target.user_id}`}
          >
            <Sparkles className="h-3 w-3 mr-1" />
            Pick this target
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const AdminIntroductionDetailPage = () => {
  const { intro_id: introId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState('');
  const [targetId, setTargetId] = useState('');
  const [tab, setTab] = useState('overview');

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get(
        `/admin/introductions/${introId}/workflow`,
      );
      setDetail(data);
      setNotes(data.introduction.admin_notes || '');
      setTargetId(data.introduction.identified_target_user_id
                     || '');
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || 'Could not load introduction workflow.');
    } finally { setBusy(false); }
  }, [introId]);

  useEffect(() => { load(); }, [load]);

  const pickTarget = async (target) => {
    setBusy(true);
    try {
      // Bundle 134 P0-B - atomic Pick target endpoint. Sets
      // identified_target_user_id, transitions pending_review
      // to identifying, writes audit entry in a single call.
      const { data } = await api.post(
        `/admin/introductions/${introId}/pick-target`,
        { target_user_id: target.user_id },
      );
      const label = data?.identified_target_display_name
        || `${target.first_name || ''} ${target.last_name || ''}`.trim()
        || target.user_id;
      toast.success(`Picked ${label}. Status: ${data?.status}`);
      await load();
      setTab('overview');
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || 'Could not pick target.');
    } finally { setBusy(false); }
  };

  const saveNotes = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/introductions/${introId}`, {
        admin_notes: notes,
        identified_target_user_id: targetId || null,
      });
      toast.success('Notes saved.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setBusy(false); }
  };

  const dismiss = async () => {
    if (!window.confirm('Dismiss this introduction? Recorded in audit.')) return;
    setBusy(true);
    try {
      await api.post(
        `/admin/introductions/${introId}/transition`,
        { to_status: 'invalidated',
          note: notes || 'admin dismissed from detail page' },
      );
      toast.success('Dismissed.');
      navigate('/admin/introductions');
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || 'Dismiss failed.');
    } finally { setBusy(false); }
  };

  // Bundle 153a - confirm-target one-click flip. Target was
  // already picked; this closes the "stuck in identifying" gap.
  const confirmTarget = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/introductions/${introId}/transition`,
        { to_status: 'target_matched',
          note: 'Admin confirmed target selection' },
      );
      toast.success(`Target confirmed. Status: ${data?.introduction?.status || 'target_matched'}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || 'Confirm target failed.');
    } finally { setBusy(false); }
  };

  const [manualMethodOn, setManualMethodOn] = useState(false);
  const [manualMethod, setManualMethod] = useState('matchmaker_manual_text');
  const [manualMethodNote, setManualMethodNote] = useState('');

  // Bundle 153b state.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmDecline, setConfirmDecline] = useState(false);
  const [confirmNote, setConfirmNote] = useState('');
  const [evidenceUploadId, setEvidenceUploadId] = useState('');

  const sendTargetInvite = async () => {
    setBusy(true);
    try {
      await api.post(
        `/admin/introductions/${introId}/send-target-invite`,
        {});
      toast.success('Invite email sent.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed.');
    } finally { setBusy(false); }
  };
  const sendIntro = async () => {
    setBusy(true);
    try {
      await api.post(
        `/admin/introductions/${introId}/send-intro`, {});
      toast.success('Contact info shared with both parties.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed.');
    } finally { setBusy(false); }
  };
  const submitConfirmConnection = async () => {
    setBusy(true);
    try {
      await api.post(
        `/admin/introductions/${introId}/confirm-connection`,
        { decline: confirmDecline,
          decline_note: confirmNote,
          evidence_upload_id: evidenceUploadId || null });
      toast.success(confirmDecline
        ? 'Declined.'
        : 'Connection confirmed.');
      setConfirmOpen(false);
      setConfirmDecline(false);
      setConfirmNote('');
      setEvidenceUploadId('');
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Confirm failed.';
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const saveManualMethod = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/introductions/${introId}/set-manual-method`,
        { intro_method: manualMethod, note: manualMethodNote },
      );
      toast.success(`Intro method set to ${data?.intro_method}.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || 'Set method failed.');
    } finally { setBusy(false); }
  };

  const resendSuggestion = async () => {
    setBusy(true);
    try {
      await api.post(
        `/admin/introductions/${introId}/resend-suggestion`,
        {});
      toast.success('Suggestion email resent.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || 'Resend failed.');
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (detail?.introduction?.intro_method
        && detail.introduction.intro_method !== 'emergent_platform') {
      setManualMethodOn(true);
      setManualMethod(detail.introduction.intro_method);
    }
  }, [detail?.introduction?.intro_method]);

  if (!detail) {
    return (
      <div className="p-8 text-sm text-neutral-500"
            data-testid="intro-detail-loading">
        {busy ? 'Loading introduction workflow.' : ''}
      </div>
    );
  }

  const intro = detail.introduction;
  const suggestions = detail.suggested_targets || [];
  const audit = detail.audit_trail || [];

  return (
    <>
    <div className="p-6 max-w-5xl space-y-4"
          data-testid="admin-introduction-detail-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Button variant="outline" size="sm"
                onClick={() => navigate('/admin/introductions')}
                data-testid="intro-detail-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> All introductions
        </Button>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-[10px] capitalize"
                  data-testid="intro-detail-status">
            {(intro.status === 'target_matched' || intro.status === 'identified') ? 'Target matched' : String(intro.status || '').replace(/_/g, ' ')}
          </Badge>
          <span className="text-[10px] font-mono text-neutral-500">
            {intro.event_slug || intro.event_id}
          </span>
          <ContactPrefPill pref={detail.contact_preference} />
        </div>
      </div>

      {/* Bundle 153a - "Confirm target" primary CTA. Renders when
          the row is in identifying AND a target has already been
          picked (either via /pick-target or via legacy PUT). One
          click flips the row to target_matched. */}
      {intro.status === 'identifying' && intro.identified_target_user_id && (
        <div className="flex items-center justify-between gap-3 border border-green-200 bg-green-50 rounded-lg p-3">
          <div className="text-sm text-green-900">
            Target already picked. Confirm to move this intro forward.
          </div>
          <Button
            onClick={confirmTarget}
            disabled={busy}
            className="bg-green-600 hover:bg-green-700 text-white"
            data-testid="intro-confirm-target-btn"
          >
            Confirm target
          </Button>
        </div>
      )}

      {/* Bundle 153b - state-driven action rail. */}
      {intro.status === 'target_matched' && intro.intro_method === 'emergent_platform' && (
        <div className="flex items-center justify-between gap-3 border border-green-200 bg-green-50 rounded-lg p-3">
          <div className="text-sm text-green-900">
            Target is matched. Send the double-opt-in invite email.
          </div>
          <Button onClick={sendTargetInvite} disabled={busy}
                       className="bg-green-600 hover:bg-green-700 text-white"
                       data-testid="intro-send-target-invite-btn">
            Send target invite
          </Button>
        </div>
      )}
      {intro.status === 'target_invited' && (
        <div className="flex items-center justify-between gap-3 border border-neutral-200 bg-neutral-50 rounded-lg p-3">
          <div className="text-sm text-neutral-700">
            Awaiting target opt-in response.
            {intro.target_invite_sent_at && (
              <span className="block text-[11px] text-neutral-500">
                Sent {new Date(intro.target_invite_sent_at).toLocaleString()}. Resend allowed after 48h.
              </span>
            )}
          </div>
          <Button onClick={sendTargetInvite} disabled={busy}
                       variant="outline"
                       data-testid="intro-resend-target-invite-btn">
            Resend invite
          </Button>
        </div>
      )}
      {intro.status === 'target_opted_in' && (
        <div className="flex items-center justify-between gap-3 border border-green-200 bg-green-50 rounded-lg p-3">
          <div className="text-sm text-green-900">
            Target opted in. Send contact info to both parties.
          </div>
          <Button onClick={sendIntro} disabled={busy}
                       className="bg-green-600 hover:bg-green-700 text-white"
                       data-testid="intro-send-intro-btn">
            Send intro
          </Button>
        </div>
      )}
      {(intro.status === 'intro_sent'
          || (intro.status === 'target_matched'
              && intro.intro_method !== 'emergent_platform')) && (
        <div className="flex items-center justify-between gap-3 border border-green-200 bg-green-50 rounded-lg p-3">
          <div className="text-sm text-green-900">
            {intro.intro_method !== 'emergent_platform'
              ? 'Manual-outside intro. Confirm to close the loop.'
              : 'Contact shared. Confirm once you know the intro happened.'}
          </div>
          <Button onClick={() => setConfirmOpen(true)} disabled={busy}
                       className="bg-green-600 hover:bg-green-700 text-white"
                       data-testid="intro-confirm-connection-btn">
            Confirm connection made
          </Button>
        </div>
      )}
      {(intro.status === 'success_confirmed'
          || intro.status === 'success_declined'
          || intro.status === 'target_opted_out') && (
        <div className={`rounded-lg p-3 text-sm ${
          intro.status === 'success_confirmed'
            ? 'bg-green-50 border border-green-200 text-green-900'
            : 'bg-red-50 border border-red-200 text-red-900'
        }`} data-testid="intro-terminal-banner">
          <div className="font-semibold capitalize">
            {intro.status.replace(/_/g, ' ')}
          </div>
          {intro.decline_note && (
            <div className="text-[12px] mt-1">
              Note: {intro.decline_note}
            </div>
          )}
        </div>
      )}

      {/* Bundle 153a - awaiting-reporter banner. */}
      {intro.status === 'target_suggested_pending_reporter' && (
        <div className="flex items-center justify-between gap-3 border border-amber-200 bg-amber-50 rounded-lg p-3" data-testid="intro-awaiting-reporter-banner">
          <div className="text-sm text-amber-900">
            Awaiting reporter confirmation on the suggested target.
            {intro.suggested_target?.email_sent_at && (
              <span className="text-[11px] text-amber-700 block">
                Sent {new Date(intro.suggested_target.email_sent_at).toLocaleString()}.
              </span>
            )}
          </div>
          <Button
            onClick={resendSuggestion}
            disabled={busy}
            variant="outline"
            className="border-amber-300 text-amber-900"
            data-testid="intro-resend-suggestion-btn"
          >
            Resend suggestion email
          </Button>
        </div>
      )}

      {/* Bundle 153a - manual-method checkbox. Once toggled on,
          the double-opt-in email actions are hidden (they'd be a
          duplicate since the intro already happened outside the
          platform). Actual close-flow ships in 153b. */}
      <div className="border rounded-lg p-3 space-y-2" data-testid="intro-manual-method-card">
        <label className="flex items-center gap-2 text-sm font-semibold text-primary-900">
          <input
            type="checkbox"
            checked={manualMethodOn}
            onChange={(e) => setManualMethodOn(e.target.checked)}
            data-testid="intro-manual-method-checkbox"
          />
          Made intro outside the platform (text / DM / in-person)
        </label>
        {manualMethodOn && (
          <div className="grid gap-2 sm:grid-cols-[1fr_2fr_auto] items-start">
            <select
              value={manualMethod}
              onChange={(e) => setManualMethod(e.target.value)}
              className="border rounded px-2 py-1.5 text-sm"
              data-testid="intro-manual-method-select"
            >
              <option value="matchmaker_manual_text">Text</option>
              <option value="matchmaker_manual_dm">DM</option>
              <option value="matchmaker_manual_call">Call</option>
              <option value="matchmaker_manual_inperson">In-person</option>
            </select>
            <Input
              value={manualMethodNote}
              onChange={(e) => setManualMethodNote(e.target.value)}
              placeholder="Optional note for the audit trail."
              data-testid="intro-manual-method-note"
            />
            <Button
              onClick={saveManualMethod}
              disabled={busy}
              className="bg-primary-800 hover:bg-primary-900 text-white"
              data-testid="intro-manual-method-save-btn"
            >
              Save method
            </Button>
          </div>
        )}
        {intro.intro_method && intro.intro_method !== 'emergent_platform' && (
          <div className="text-[11px] text-amber-800">
            This intro is marked as <strong>{intro.intro_method.replace(/^matchmaker_manual_/, '')}</strong>.
            The double-opt-in email actions are hidden. Complete via the manual close flow (Bundle 153b).
          </div>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="intro-detail-tabs">
          <TabsTrigger value="overview"
                          data-testid="intro-tab-overview">
            Overview
          </TabsTrigger>
          <TabsTrigger value="suggestions"
                          data-testid="intro-tab-suggestions">
            Target suggestions ({suggestions.length})
          </TabsTrigger>
          <TabsTrigger value="audit"
                          data-testid="intro-tab-audit">
            Audit trail ({audit.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 pt-4">
          <ReporterCard reporter={detail.reporter} />
          <NoticedPanel noticed={detail.what_they_noticed} />
          <Card><CardContent className="p-4 space-y-3">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">
              Admin notes
            </div>
            <Textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Vetting notes, phone call outcome, etc."
              data-testid="intro-detail-notes"
            />
            <div className="grid gap-2 sm:grid-cols-[2fr_1fr]">
              <Input
                value={targetId}
                onChange={(e) => setTargetId(e.target.value)}
                placeholder="Identified target user id (optional)"
                data-testid="intro-detail-target-id"
              />
              <Button
                onClick={saveNotes}
                disabled={busy}
                className="bg-primary-800 text-white hover:bg-primary-900"
                data-testid="intro-detail-save-btn"
              >
                Save curation
              </Button>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <Button
                variant="outline" size="sm"
                onClick={() => setTab('suggestions')}
                data-testid="intro-detail-open-suggestions-btn"
              >
                <UsersIcon className="h-3 w-3 mr-1" />
                Open suggestions
              </Button>
              <Button
                variant="outline" size="sm"
                onClick={dismiss}
                disabled={busy}
                data-testid="intro-detail-dismiss-btn"
              >
                <XCircle className="h-3 w-3 mr-1" />
                Dismiss
              </Button>
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="suggestions" className="pt-4 space-y-3">
          {suggestions.length === 0 ? (
            <div className="text-sm text-neutral-500 p-6 text-center border rounded"
                  data-testid="intro-suggestions-empty">
              No candidate suggestions yet. Run the suggester on the
              global introductions list to seed candidates.
            </div>
          ) : (
            suggestions.map((s) => (
              <SuggestionCard
                key={s.user_id}
                target={s}
                onPick={pickTarget}
                picking={busy}
              />
            ))
          )}
        </TabsContent>

        <TabsContent value="audit" className="pt-4">
          <Card><CardContent className="p-4">
            {audit.length === 0 ? (
              <div className="text-sm text-neutral-500"
                    data-testid="intro-audit-empty">
                No audit entries yet.
              </div>
            ) : (
              <table className="w-full text-xs"
                      data-testid="intro-audit-table">
                <thead className="text-neutral-500 uppercase text-[10px]">
                  <tr>
                    <th className="text-left p-2">When</th>
                    <th className="text-left p-2">Actor</th>
                    <th className="text-left p-2">From</th>
                    <th className="text-left p-2">To</th>
                    <th className="text-left p-2">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.map((a) => (
                    <tr key={a.id} className="border-t border-neutral-100">
                      <td className="p-2 text-neutral-500">
                        {a.at ? new Date(a.at).toLocaleString() : '-'}
                      </td>
                      <td className="p-2 text-primary-900">
                        {a.actor_type} {a.actor_id ? `(${a.actor_id.slice(0, 6)})` : ''}
                      </td>
                      <td className="p-2">{a.from_status || '-'}</td>
                      <td className="p-2">{a.to_status || '-'}</td>
                      <td className="p-2 text-neutral-700">{a.note || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
    <ConfirmConnectionModal
      open={confirmOpen}
      onClose={() => setConfirmOpen(false)}
      decline={confirmDecline}
      setDecline={setConfirmDecline}
      note={confirmNote}
      setNote={setConfirmNote}
      evidenceUploadId={evidenceUploadId}
      setEvidenceUploadId={setEvidenceUploadId}
      onSubmit={submitConfirmConnection}
      busy={busy}
    />
    </>
  );
};

const ConfirmConnectionModal = ({
  open, onClose, decline, setDecline, note, setNote,
  evidenceUploadId, setEvidenceUploadId, onSubmit, busy,
}) => {
  const uploadEvidence = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('owner_type', 'intro_evidence_bundle_153b');
    fd.append('is_public', 'false');
    try {
      const { data } = await api.post(
        '/media/upload/v2', fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      const id = data?.data?.id || data?.id;
      if (id) {
        setEvidenceUploadId(id);
        toast.success('Evidence attached.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail
        || 'Upload failed.');
    }
  };
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="confirm-connection-modal">
      <div className="bg-white rounded-lg p-6 max-w-md w-full space-y-4">
        <h2 className="font-display text-xl text-primary-900">
          Confirm connection made
        </h2>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={decline}
                    onChange={(e) => setDecline(e.target.checked)}
                    data-testid="confirm-decline-checkbox" />
          Mark as declined instead
        </label>
        {decline && (
          <textarea rows={3}
                       value={note}
                       onChange={(e) => setNote(e.target.value)}
                       placeholder="Why did this not work?"
                       className="w-full border rounded p-2 text-sm"
                       data-testid="confirm-decline-note" />
        )}
        {!decline && (
          <div className="space-y-2">
            <label className="text-xs text-neutral-600 block">
              Attach evidence (required for events with 20+ intros on manual-outside intros)
            </label>
            <input type="file" onChange={uploadEvidence}
                       data-testid="confirm-evidence-file" />
            {evidenceUploadId && (
              <div className="text-[11px] text-green-700">
                Attached (id {evidenceUploadId.slice(0, 8)}...).
              </div>
            )}
          </div>
        )}
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}
                       data-testid="confirm-cancel-btn">Cancel</Button>
          <Button onClick={onSubmit} disabled={busy}
                       className={`${decline ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'} text-white flex-1`}
                       data-testid="confirm-submit-btn">
            {decline ? 'Submit decline' : 'Confirm connection'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminIntroductionDetailPage;
